/**
 * jobMoney.js — One definition of "does this job's money count".
 *
 * Every surface that totals job revenue imports from here. The audit of the
 * Jobs money strip found the same concept computed six different ways across
 * the app, disagreeing by thousands on the same month; this module exists so
 * the Jobs page cannot add a seventh.
 *
 * The contract, decided by Cyprian 2026-08-14:
 *   - Voided work never counts, including jobs the auto-archive sweep has
 *     since flipped from 'void' to 'archived'.
 *   - Work counts from 'confirmed' onward. 'intake' and 'quoted' are pipeline:
 *     a quote is a number we hope for, and totalling it reads as money in hand.
 *
 * This is an ESTIMATE built from the jobs table alone. It is not cash
 * accounting and reads no invoices.
 */
import { FLOW } from './statusTransitions';
import { normalizeSnapshot, getAllEarnings } from './calcEngine';

const BOOKED_FROM = FLOW.indexOf('confirmed');

/**
 * Voided money never counts. Status alone is not enough: the auto-archive
 * sweep in server/routes/jobs.js flips void → archived (archived_from_void)
 * after N days, so a status-only test lets a killed job walk back into the
 * totals days later, at 100% margin since its crew ledger was forfeited.
 */
export function isVoided(job) {
  return job?.status === 'void' || job?.archivedFromVoid === true;
}

/** Confirmed or later, and not voided. */
export function isBooked(job) {
  if (!job || isVoided(job)) return false;
  const idx = FLOW.indexOf(job.status);
  return idx >= BOOKED_FROM;
}

/** Sum of revenue across the jobs whose money counts. */
export function bookedRevenue(jobs) {
  let total = 0;
  for (const j of jobs || []) {
    if (isBooked(j)) total += parseFloat(j.revenue) || 0;
  }
  return total;
}

function pad(n) { return String(n).padStart(2, '0'); }

/**
 * 'YYYY-MM-DD' in the VIEWER's timezone.
 *
 * shoot_date / delivery_date are DATE columns serialized as local midnight
 * (e.g. "2026-08-26T07:00:00.000Z" from a Pacific backend), so parsing them as
 * a Date and reading local fields gives the calendar day back. created_at is a
 * real timestamp, and slicing its ISO string would bucket a job created 7pm
 * Santa Barbara into the next day (and, on the 31st, the next month).
 */
export function localDateStr(v) {
  if (!v) return null;
  const s = String(v);
  if (!s.includes('T')) return s.slice(0, 10);
  const dt = new Date(s);
  if (isNaN(dt.getTime())) return null;
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
}

/**
 * The date a job's money lands on: delivery, else shoot, else created.
 *
 * A delivery date is trusted only when it is plausible. Three jobs carry typo'd
 * delivery dates (2013, 2016, 2020) against real 2026 shoots, and since
 * delivery wins the race they banish $1,300 of revenue to months nobody will
 * ever scroll to. A delivery date that precedes its own shoot is a typo, not a
 * fact, so it falls through to the shoot date instead of being obeyed.
 *
 * Shared by the money strip and the Jobs list's archived month headers so the
 * two can never file the same job under different months.
 */
export function jobDate(job) {
  const shoot = localDateStr(job.shootDate || job.shootDates?.[0]);
  const delivery = localDateStr(job.deliveryDate);
  const deliveryOk = delivery && (!shoot || delivery >= shoot);
  return (deliveryOk ? delivery : null) || shoot || localDateStr(job.createdAt) || null;
}

// ── Crew cost ───────────────────────────────────────────────────────────────

/**
 * The ledger person whose pay is NOT a cost — the owner's cut stays in profit.
 * Lived as a private copy inside JobsMoneyCounter; it belongs here so every
 * surface that asks "what did this job cost in crew" answers the same way.
 */
const OWNER_LEDGER_NAME = 'cyprian lewicki';

export function isOwner(person) {
  return (person || '').trim().toLowerCase() === OWNER_LEDGER_NAME;
}

const r2 = (n) => Math.round(n * 100) / 100;

// job_earnings_ledger.role → department bucket. The two hourly roles are
// declared in the engine but have never once been written to the table.
export const LEDGER_BUCKET = {
  originator: 'orig',
  prod_lead: 'prod', prod_hourly: 'prod',
  post_lead: 'post', post_hourly: 'post',
};

/**
 * What a job actually costs in crew, counting budgeted hourly work as real.
 *
 * THE PROBLEM THIS SOLVES
 * Nobody logs time — the time_entries table is empty — so every hourly crew
 * member is estimate-only. The payout ledger deliberately refuses to pay an
 * estimate (payoutEngine passes estFallback:false), so no hourly row has ever
 * been written. The side lead earns `budget − hourlyTotal`, which with a zero
 * hourly total means the lead's row quietly swallows the hourly budget. When
 * that lead is the owner, the owner filter then drops it and the cost vanishes
 * from the strip entirely: Cars & Cowboys showed $37.50 of crew on a job that
 * had Yan Duero budgeted for $375 of it.
 *
 * THE RULE: per-BUCKET max(ledger, snapshot). Not a sum, not a per-person max.
 * Within one department the snapshot is conserved by construction
 * (leadEarned = budget − hourlyTotal), so each estimated dollar appears in the
 * snapshot bucket exactly once — and in the ledger bucket exactly once too,
 * hidden inside the lead's row. max() therefore picks the right one every time:
 *
 *   non-owner lead absorbed it  → ledger already counts it → no change
 *   OWNER absorbed it           → ledger bucket short → take the snapshot
 *   no lead at all              → ledger bucket empty → take the snapshot
 *   ledger drifted above        → floor at what payroll actually committed
 *
 * Adding the two sources instead would double count by $8,816 across the booked
 * set; a per-PERSON max double counts $461 and can push a job's cost past its
 * own revenue. The bucket is the required unit — see the New Vic jobs, where
 * Sierra's lead row already contains Calvin's hourly money.
 *
 * DO NOT "finish the job" by flipping estFallback to true in payoutEngine.
 * That is a payout change, not a cost change, and it is unsafe:
 * recomputePending locks (person, role) keys that are already earned or paid,
 * so a new hourly row inserts while the offsetting lead reduction is silently
 * discarded — $536 of un-offset payable against lead rows that cannot shrink,
 * $1,112 of which is already cash out the door. The cost problem is solved
 * here, at the display layer, on purpose.
 *
 * Never branch arithmetic on `isEstimated`. It is stamped at snapshot-save time
 * and never refreshed, so it stays true forever once hours are eventually
 * logged. It is safe only for explanatory copy.
 *
 * @param {object} job                  job row, needs .calcSnapshot
 * @param {object[]} ledgerRows         job_earnings_ledger rows for THIS job (camelCase)
 * @param {object[]} entries            time_entries for THIS job (mileage only)
 * @param {boolean} [opts.excludeOwner] true for the Jobs strip
 */
export function jobCrewCost(job, ledgerRows = [], entries = [], { excludeOwner = true } = {}) {
  const L = { orig: 0, prod: 0, post: 0, other: 0 };
  const S = { orig: 0, prod: 0, post: 0, other: 0 };

  // Committed money.
  for (const r of ledgerRows) {
    if (r.state === 'forfeited') continue;              // voided work never counts
    if (excludeOwner && isOwner(r.person)) continue;
    L[LEDGER_BUCKET[r.role] || 'other'] += parseFloat(r.amount) || 0;
  }

  // Budgeted money, estimates included. normalizeSnapshot covers all three
  // payModes: items rebuilds prod/post from itemAllocs, staycation and legacy
  // fall through the passthrough, V1 flat snapshots convert.
  const ns = normalizeSnapshot(job?.calcSnapshot);
  for (const e of getAllEarnings(ns)) {
    if (excludeOwner && isOwner(e.name)) continue;
    S[e.role === 'prod' || e.role === 'post' || e.role === 'orig' ? e.role : 'other'] += e.earned || 0;
  }

  // Mileage is in neither source, so it is additive and cannot double count.
  let mileage = 0;
  for (const t of entries) {
    if (excludeOwner && isOwner(t.person)) continue;
    mileage += parseFloat(t.mileageReimbursement) || 0;
  }

  const orig = Math.max(L.orig, S.orig);
  const prod = Math.max(L.prod, S.prod);
  const post = Math.max(L.post, S.post);
  const other = L.other;   // admin pool and mileage roles have no snapshot mirror

  const total = r2(orig + prod + post + other + mileage);
  const ledgerOnly = r2(L.orig + L.prod + L.post + L.other + mileage);

  return {
    orig, prod, post, other, mileage, total, ledgerOnly,
    // How much of `total` the ledger does not know about yet — for copy only.
    liftedFromSnapshot: r2(Math.max(0, total - ledgerOnly)),
  };
}
